import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { ContactsApi } from 'entities/Contacts'
import type { Contact } from 'entities/Contacts/model/Contact'
import type { IParamsContacts } from 'entities/Contacts/api/types'
import type { IParamsFiltersListGroups } from 'entities/Broadcast/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RowContact } from 'entities/Contacts/model/Table/RowContact'

const CONTACTS_PAGE_SIZE = 20

export class PowerDialerContactsStore {
  private _contactsMap: Map<number, Contact> = new Map()
  private _nextPage = 1
  private _lastPage = Infinity
  private _deletedIds: Set<number> = new Set()
  private _hasInitializedContacts = false
  private _isLoadingMore = false
  private _fetchContactsParams: IParamsContacts = {}
  private readonly _tableStore: TableStore<RowContact>

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._tableStore = new TableStore({
      key: 'power_dialer_editor',
    })
  }

  get tableStore() {
    return this._tableStore
  }

  private _fetchContactsList = async ({ page = 1, ...rest }: IParamsContacts = {}): Promise<
    Contact[]
  > => {
    try {
      const { data } = await ContactsApi.getContactsList({
        ...rest,
        page,
        sortOrder: 'desc',
        sortBy: ['created_at'],
        length: CONTACTS_PAGE_SIZE,
      })

      runInAction(() => {
        this._tableStore.setTotal(data.total)
        this._lastPage = data.last_page
        this._nextPage = data.current_page + 1
      })

      return this._entities.contactsStore.addItems(data.data)
    } catch (err) {
      logger.error(err)
      return []
    }
  }

  loadNextContactsPage = async () => {
    if (!this.hasMoreContacts) return []

    runInAction(() => {
      this._isLoadingMore = true
    })
    try {
      const contacts = await this._fetchContactsList({
        ...this._fetchContactsParams,
        page: this._nextPage,
      })
      runInAction(() => {
        contacts.forEach((contact) => this._contactsMap.set(contact.id, contact))
      })

      return contacts
    } catch (error) {
      logger.error(error)
      return []
    } finally {
      runInAction(() => {
        this._isLoadingMore = false
      })
    }
  }

  startPagination = async (params: IParamsContacts) => {
    this._contactsMap.clear()
    this._deletedIds.clear()
    runInAction(() => {
      this._tableStore.setTotal(0)
      this._nextPage = 1
      this._lastPage = Infinity
      this._hasInitializedContacts = false
      this._fetchContactsParams = params
    })

    try {
      return await this.loadNextContactsPage()
    } catch (error) {
      logger.error(error)
      return []
    } finally {
      this._hasInitializedContacts = true
    }
  }

  setSelectedContacts = (contacts: Contact[]) => {
    contacts.forEach((contact) => this._contactsMap.set(contact.id, contact))
  }

  deleteSelectedId = (id: number) => {
    if (this._contactsMap.delete(id)) this._deletedIds.add(id)
  }

  get contacts() {
    return [...this._contactsMap.values()]
  }

  get contactsRows() {
    return this.contacts.map((item) => new RowContact(item))
  }

  get contactsCount() {
    return this._contactsMap.size
  }

  get deletedContactsCount() {
    return this._deletedIds.size
  }

  get hasMoreContacts() {
    if (!this._hasInitializedContacts) return true
    return this._nextPage <= this._lastPage
  }

  get total() {
    return this._tableStore.total
  }

  get totalSelectedContacts() {
    if (this.total === null) return 0

    return Math.max(this.total - this.deletedContactsCount, 0)
  }

  get isLoadingMore() {
    return this._isLoadingMore
  }

  get contactsFiltersParams(): IParamsFiltersListGroups {
    const { filtersList, filtersListGroups, excludeFiltersListGroups, search } =
      this._fetchContactsParams ?? {}
    const hasCustomFilters =
      Boolean(filtersListGroups?.tags?.length) ||
      Boolean(filtersListGroups?.segments?.length) ||
      Boolean(filtersListGroups?.advanced?.length)
    let mergedFiltersList = filtersList
    const trimmedSearch = search?.trim()
    const searchFilter = trimmedSearch
      ? {
          key: 'global_search',
          operator: 'equals',
          value: trimmedSearch,
        }
      : null

    if (searchFilter) {
      if (filtersList?.length) {
        const [head, ...tail] = filtersList
        mergedFiltersList = [
          {
            ...head,
            filters: [...(head.filters ?? []), searchFilter],
          },
          ...tail,
        ]
      } else {
        mergedFiltersList = [
          {
            filters: [searchFilter],
            custom_filters: [],
            filter_tags: [],
          },
        ]
      }
    }

    if (mergedFiltersList) {
      return {
        filtersListGroups: {
          advanced: [
            {
              filtersList: mergedFiltersList,
            },
          ],
        },
        excludeFiltersListGroups,
      }
    }

    if (filtersListGroups && hasCustomFilters) {
      return {
        filtersListGroups,
        excludeFiltersListGroups,
      }
    }

    const originalContacts = filtersListGroups?.contacts ?? []
    const filteredContacts = originalContacts.filter((id) => !this._deletedIds.has(id))

    return {
      filtersListGroups: {
        contacts: filteredContacts,
        tags: [],
        segments: [],
        advanced: [],
      },
      excludeFiltersListGroups,
    }
  }

  get isOnlyManualContactsSelection() {
    const { filtersListGroups } = this.contactsFiltersParams
    const { contacts = [], tags, segments, advanced } = filtersListGroups ?? {}

    return contacts.length > 0 && !tags?.length && !segments?.length && !advanced?.length
  }

  reset = () => {
    this._contactsMap.clear()
    this._deletedIds.clear()
    this._nextPage = 1
    this._lastPage = Infinity
    this._tableStore.dispose()
    this._hasInitializedContacts = false
    this._fetchContactsParams = {}
    this._isLoadingMore = false
  }
}
